import axios from 'axios';

import { createErr, createOk, Result } from 'types/result';
import {
  Condition,
  FetchNotificationsApiResponse,
  isFetchNotificationApiResponse,
  LEVELS,
  isOriginalNotification,
  OriginalNotification,
  CONDITIONS,
} from 'types/notification';
import { CustomerForNotification } from 'types/customer';
import { constructUrl } from './utils';
import { convertFormDatetimeIntoISOString } from '../utils/dateUtils';

const URL = '/v1/notifications';

/**
 * お知らせ一覧を取得する
 * @param page 何ページ目か
 * @returns page ページに含まれるお知らせ一覧
 */
export const fetchNotifications = async (
  page: number,
): Promise<Result<FetchNotificationsApiResponse, Error>> => {
  const url = constructUrl(URL);

  return axios
    .get(url, {
      params: {
        page,
      },
    })
    .then((res) => {
      const data = res.data as unknown;

      if (!isFetchNotificationApiResponse(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * お知らせを作成する
 * @param params お知らせ作成に必要なパラメータ
 * @returns 作成されたお知らせ
 */
export const postNotification = async (
  params: postNotificationParams,
): Promise<Result<OriginalNotification, Error>> => {
  const url = constructUrl(URL);

  return axios
    .post(url, {
      ...params,
    })
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalNotification(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

export type postNotificationParams = {
  title: string;
  message: string;
  level: string;
  startsAt: string;
  endsAt: string;
  condition: Condition;
  targets: number[];
};

const createTargets = (
  condition: Condition,
  selectedCustomers: CustomerForNotification[],
) => {
  if (condition === CONDITIONS.ALL) {
    return [];
  }

  return selectedCustomers.map((customer) => customer.id);
};

export const createPostNotificationParams = (
  title: string,
  message: string,
  isHigh: boolean,
  startsAt: string,
  endsAt: string,
  condition: Condition,
  selectedCustomers: CustomerForNotification[],
) => {
  const returnValue: postNotificationParams = {
    title,
    message,
    level: isHigh ? LEVELS.HIGH : LEVELS.NORMAL,
    startsAt: convertFormDatetimeIntoISOString(startsAt),
    endsAt: convertFormDatetimeIntoISOString(endsAt),
    condition,
    targets: createTargets(condition, selectedCustomers),
  };

  return returnValue;
};

/**
 * お知らせを更新する
 * @param id 更新するお知らせの ID
 * @param params 更新するお知らせのパラメータ
 * @returns 更新されたお知らせ
 */
export const putNotification = async (
  id: number,
  params: putNotificationParams,
): Promise<Result<OriginalNotification, Error>> => {
  const url = `${constructUrl(URL)}/${id}`;

  return axios
    .put(url, {
      ...params,
    })
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalNotification(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

export type putNotificationParams = {
  title: string;
  message: string;
  level: string;
  startsAt: string;
  endsAt: string;
  condition: Condition;
  targets: number[];
};

export const createPutNotificationParams = (
  title: string,
  message: string,
  isHigh: boolean,
  startsAt: string,
  endsAt: string,
  condition: Condition,
  selectedCustomers: CustomerForNotification[],
) => {
  const returnValue: putNotificationParams = {
    title,
    message,
    level: isHigh ? LEVELS.HIGH : LEVELS.NORMAL,
    startsAt: convertFormDatetimeIntoISOString(startsAt),
    endsAt: convertFormDatetimeIntoISOString(endsAt),
    condition,
    targets: createTargets(condition, selectedCustomers),
  };

  return returnValue;
};

/**
 * お知らせを削除する
 * @param id 削除するお知らせの ID
 * @returns null
 */
export const deleteNotification = async (
  id: number,
): Promise<Result<null, Error>> => {
  const url = `${constructUrl(URL)}/${id}`;

  return axios
    .delete(url)
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};
